/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { ModalBody, ModalHeader } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import { getDynamicImageSettings, getResponsiveImage } from '../../../Utilities/get-dynamic-image';

interface IShoppingIntentModal {
    form: JSX.Element;
    errorMsgHeading?: JSX.Element;
    errorBlock: JSX.Element;
    errorCTA?: JSX.Element;
    emailValidationBlock: boolean | JSX.Element;
    isSubscribeSuccessfully: boolean;
    isError: boolean;
    onCloseModal?(): void;
}

interface IShoppingModalData {
    title1: string,
    redText: string,
    title2: string,
    subtitle: string,
    imageData: IImageData,
    buttonSelection: {text: string}[],
    disclosure: string,
    emailModalData: IEmailModalData,
    answerCardData: any[]
}

interface IImageData {
    desktop?: {src: string},
    tablet?: {src: string},
    mobile?: {src: string},
    default: {src: string, altText?: string}
}

interface IEmailModalData {
    subtitle: string,
    title: string,
    imageData: IImageData
}

interface ICalloutCardData {
    calloutCard: boolean,
    title: string,
    promoCode: string,
    imageData: IImageData,
    cardSubtitle: string,
    cardTitle: string,
    cardDescription: string,
    cardButton: string,
    cardLink: string,
    disclosure: string
}

interface IShopNowData {
    calloutCard: boolean,
    title: string,
    promoCode: string,
    description: string,
    disclosure: string,
    cardData: IShopNowCardData[]
}

interface IShopNowCardData {
    imageData: IImageData,
    title: string,
    link: string
}

export default (props: IShoppingIntentModal) => {
    const styles = 'modal-shopping__';
    const {
        form,
        errorMsgHeading,
        errorBlock,
        errorCTA,
        emailValidationBlock,
        isSubscribeSuccessfully,
        isError,
        onCloseModal
    } = props;

    const [showShoppingModal, setShowShoppingModal] = React.useState<boolean>(true);
    const [showEmailModal, setShowEmailModal] = React.useState<boolean>(false);
    const [shopButtonIndex, setShopButtonIndex] = React.useState<number>(0);

    const shoppingModalData = {
        title1: 'Answer to get ',
        redText: '$50 OFF* ',
        title2: 'your next purchase',
        subtitle: 'How will you shop?',
        imageData: {
            default: {src: 'https://images-us-prod.cms.dynamics365commerce.ms/cms/api/cncgmclkfv/imageFileData/MAmzAd?pubver=1', altText: 'How will you shop'}
        },
        buttonSelection: [
            {text: 'Online'},
            {text: 'In-Store'},
            {text: 'Don’t Know Yet'}
        ],
        disclosure: '*Your first order of $499+. Some exclusions apply.',
        emailModalData: {
            subtitle: 'Almost there!',
            title: 'What email should we send the discount to?',
            imageData: {
                default: {src: 'https://images-us-prod.cms.dynamics365commerce.ms/cms/api/cncgmclkfv/imageFileData/MAmzAd?pubver=1', altText: 'How will you shop'}
            }
        },
        answerCardData: [
            {
                calloutCard: true,
                title: 'Your discount code is:',
                promoCode: 'SLEEP',
                imageData: {
                    desktop: {src: 'https://images-us-prod.cms.dynamics365commerce.ms/cms/api/cncgmclkfv/imageFileData/MAmzAg?pubver=1'},
                    tablet: {src: 'https://images-us-prod.cms.dynamics365commerce.ms/cms/api/cncgmclkfv/imageFileData/MAmx1F?pubver=1'},
                    mobile: {src: 'https://images-us-prod.cms.dynamics365commerce.ms/cms/api/cncgmclkfv/imageFileData/MAmx1F?pubver=1'},
                    default: {src: 'https://images-us-prod.cms.dynamics365commerce.ms/cms/api/cncgmclkfv/imageFileData/MAmzAg?pubver=1', altText: 'MattressMatcher'}
                },
                cardSubtitle: 'MATTRESSMATCHER® ',
                cardTitle: 'Find Your Best Sleep',
                cardDescription: 'Take our quick quiz & we’ll match you with the perfect bed.',
                cardButton: 'Start Now',
                cardLink: 'https://www.mattressfirm.com/mattressmatcher',
                disclosure: '*Your discount code has also been sent to your email'
            },
            {
                calloutCard: true,
                title: 'Your discount code is:',
                promoCode: 'DREAM',
                imageData: {
                    desktop: {src: 'https://images-us-prod.cms.dynamics365commerce.ms/cms/api/cncgmclkfv/imageFileData/MAmpgC?pubver=1'},
                    tablet: {src: 'https://images-us-prod.cms.dynamics365commerce.ms/cms/api/cncgmclkfv/imageFileData/MAmrQc?pubver=1'},
                    mobile: {src: 'https://images-us-prod.cms.dynamics365commerce.ms/cms/api/cncgmclkfv/imageFileData/MAmrQc?pubver=1'},
                    default: {src: 'https://images-us-prod.cms.dynamics365commerce.ms/cms/api/cncgmclkfv/imageFileData/MAmpgC?pubver=1', altText: 'Shop In-Store'}
                },
                cardSubtitle: 'SHOP IN-STORE',
                cardTitle: 'We’re here to help',
                cardDescription: 'Visit us in-store to meet with a Sleep Expert® and discover America’s best brands.',
                cardButton: 'Find a Store',
                cardLink: 'https://www.mattressfirm.com/en-us/stores/',
                disclosure: '*Your discount code has also been sent to your email'
            },
            {
                calloutCard: false,
                title: 'Your discount code is:',
                promoCode: 'ZZZS',
                description: 'We think you might like these:',
                disclosure: '*Your discount code has also been sent to your email',
                cardData: [
                    {
                        imageData: {
                            desktop: {src: 'https://images-us-prod.cms.dynamics365commerce.ms/cms/api/cncgmclkfv/imageFileData/MAmx1C?pubver=1'},
                            tablet: {src: 'https://images-us-prod.cms.dynamics365commerce.ms/cms/api/cncgmclkfv/imageFileData/MAmpgF?pubver=1'},
                            mobile: {src: 'https://images-us-prod.cms.dynamics365commerce.ms/cms/api/cncgmclkfv/imageFileData/MAmpgF?pubver=1'},
                            default: {src: 'https://images-us-prod.cms.dynamics365commerce.ms/cms/api/cncgmclkfv/imageFileData/MAmx1C?pubver=1', altText: 'In Stock & Ready to Ship Mattresses'}
                        },
                        title: 'In Stock & Ready to Ship Mattresses',
                        link: 'https://www.mattressfirm.com/in-stock/5637154326.c'
                    },
                    {
                        imageData: {
                            desktop: {src: 'https://images-us-prod.cms.dynamics365commerce.ms/cms/api/cncgmclkfv/imageFileData/MAmx1I?pubver=1'},
                            tablet: {src: 'https://images-us-prod.cms.dynamics365commerce.ms/cms/api/cncgmclkfv/imageFileData/MAmrQf?pubver=1'},
                            mobile: {src: 'https://images-us-prod.cms.dynamics365commerce.ms/cms/api/cncgmclkfv/imageFileData/MAmrQf?pubver=1'},
                            default: {src: 'https://images-us-prod.cms.dynamics365commerce.ms/cms/api/cncgmclkfv/imageFileData/MAmx1I?pubver=1', altText: 'Best Selling Mattresses'}
                        },
                        title: 'Best Selling Mattresses',
                        link: 'https://www.mattressfirm.com/best-sellers/5637155076.c'
                    }
                ]
            }
        ]
    };

    return (
        <>
            {showShoppingModal ? shoppingModalHTML(shoppingModalData) : null}
            {showEmailModal ? shoppingEmailModalHTML(shoppingModalData) : null}
        </>
    );

    function shoppingButton(index: number, buttonText: string) {

        setShowShoppingModal(false);
        setShowEmailModal(true);
        setShopButtonIndex(index);
    }

    function shoppingModalHTML(shoppingModalData: IShoppingModalData) {
        return (
            <div className={`${styles}selection`}>
                <div className={`${styles}selection-aside`}>
                    <img src={shoppingModalData.imageData.default.src} alt={shoppingModalData.imageData.default.altText} />
                </div>
                <div className={`${styles}selection-body`}>
                    <ModalHeader>
                        {<button type='button' className='msc-modal__close-button' aria-label='Close' onClick={onCloseModal} />}
                    </ModalHeader>
                    <ModalBody className={`${styles}selection-content`}>
                        <div className={`${styles}selection-title`}>{shoppingModalData.title1}<span className={`${styles}selection-title-span`}>{shoppingModalData.redText}</span>{shoppingModalData.title2}</div>
                        <div className={`${styles}selection-subtitle`}>{shoppingModalData.subtitle}</div>
                        {shoppingModalData.buttonSelection.map((button: {text: string}, index: number) => {
                            return (
                                <button className={`${styles}selection-button`} key={index} onClick={() => shoppingButton(index, button.text)}>{button.text}</button>
                            );
                        })}
                        <div className={`${styles}selection-text`}>
                            <p>{shoppingModalData.disclosure}</p>
                        </div>
                    </ModalBody>
                </div>
            </div>
        );
    }

    function shoppingEmailModalHTML(shoppingModalData: IShoppingModalData) {
        return (
            <div className={!isSubscribeSuccessfully ? `${styles}email-subscription` : ''}>
                {!isSubscribeSuccessfully && (
                    <div className={`${styles}email-subscription-aside`}>
                        <img src={shoppingModalData.emailModalData.imageData.default.src} alt={shoppingModalData.emailModalData.imageData.default.altText}/>
                    </div>
                )}
                <div className={!isSubscribeSuccessfully ? `${styles}email-subscription-body` : ''}>
                    <ModalHeader>
                        {<button type='button' className='msc-modal__close-button' aria-label='Close' onClick={onCloseModal} />}
                    </ModalHeader>
                    <ModalBody className={`${!isSubscribeSuccessfully ? `${styles}email-subscription-content` : ''} ${isSubscribeSuccessfully && shoppingModalData.answerCardData[shopButtonIndex].calloutCard ? `${styles}answer-body` : ''} ${isSubscribeSuccessfully && !shoppingModalData.answerCardData[shopButtonIndex].calloutCard ? `${styles}shop-now-body` : ''}`}>
                            {/* subscription form */}
                            {!isSubscribeSuccessfully && !isError && (
                                <>
                                    <div className={`${styles}email-subscription-subtitle`}>{shoppingModalData.emailModalData.subtitle}</div>
                                    <div className={`${styles}email-subscription-title`}>{shoppingModalData.emailModalData.title}</div>
                                    {form}
                                    {emailValidationBlock}
                                </>
                            )}
                            {/* Successfully subscribed state */}
                            {isSubscribeSuccessfully && (
                                <>
                                    {shoppingModalData.answerCardData[shopButtonIndex].calloutCard ? couponCalloutCardHTML(shoppingModalData.answerCardData[shopButtonIndex]) : shopNowHTML(shoppingModalData.answerCardData[shopButtonIndex])}
                                </>
                            )}
                            {/* Error state */}
                            {isError && (
                                <div className='modal-email-subscription__error'>
                                    {errorMsgHeading}
                                    {errorBlock}
                                    {errorCTA}
                                </div>
                            )}
                    </ModalBody>
                </div>
            </div>
        );
    }

    function couponCalloutCardHTML(answerCardData: ICalloutCardData) {
        return (
            <>
                <div className={`${styles}answer-title`}>{answerCardData.title}</div>
                <div className={`${styles}answer-subtitle`}>{answerCardData.promoCode}</div>
                <div className={`${styles}answer-callout-card`}>
                    <div className={`${styles}answer-callout-card-aside`}>
                        {getResponsiveImage({
                            desktop: answerCardData.imageData.desktop,
                            tablet: answerCardData.imageData.tablet,
                            mobile: answerCardData.imageData.mobile,
                            defaultImg: answerCardData.imageData.default,
                            imageSettingViewports: getDynamicImageSettings({ moduleName: 'mfrm-hero-with-tabs' })
                        })}
                    </div>
                    <div className={`${styles}answer-callout-card-body`}>
                        <div className={`${styles}answer-callout-card-subtitle`}>{answerCardData.cardSubtitle}</div>
                        <div className={`${styles}answer-callout-card-title`}>{answerCardData.cardTitle}</div>
                        <div className={`${styles}answer-callout-card-description`}>{answerCardData.cardDescription}</div>
                        <a href={answerCardData.cardLink}>
                            <button className={`${styles}answer-callout-card-button`}>{answerCardData.cardButton}</button>
                        </a>
                    </div>
                </div>
                <div className={`${styles}answer-text`}>
                    <p>{answerCardData.disclosure}</p>
                </div>
            </>
        );
    }

    function shopNowHTML(answerCardData: IShopNowData) {
        return (
            <>
                <div className={`${styles}answer-title`}>{answerCardData.title}</div>
                <div className={`${styles}answer-subtitle`}>{answerCardData.promoCode}</div>
                <div className={`${styles}shop-now-description`}>{answerCardData.description}</div>
                <div className={`${styles}shop-now-container`}>
                    {answerCardData.cardData.map((card: {imageData: any, title: string, link: string}, index: number) => {
                        return (
                            <a href={card.link} className={`${styles}shop-now-card`} key={index}>
                                <div className={`${styles}shop-now-card-aside`}>
                                {getResponsiveImage({
                                    desktop: card.imageData.desktop,
                                    tablet: card.imageData.tablet,
                                    mobile: card.imageData.mobile,
                                    defaultImg: card.imageData.default,
                                    imageSettingViewports: getDynamicImageSettings({ moduleName: 'mfrm-hero-with-tabs' })
                                })}
                                </div>
                                <div className={`${styles}shop-now-card-body`}>
                                    <div className={`${styles}shop-now-card-title`}>{card.title}</div>
                                </div>
                            </a>
                        );
                    })}
                </div>
                <div className={`${styles}answer-text`}>
                    <p>{answerCardData.disclosure}</p>
                </div>
            </>
        );
    }
};
